'use strict';

import Helpers from './Helpers';
import DefaultFunctions from './DefaultFunctions';
import BamTeamData from 'bam-team-data';
import BamArgValidator from 'bam-arg-validator';
const cloneDeep = BamTeamData.getHelpers().cloneDeep;

/*
 * See constructor JSDocs for details about each option.  Note that some required options also have a default value.
 * This is because, for example, the default value for appView, 'web', is treated differently from any other value for
 * appView (e.g., 'app').
 */
const SUPPORTED_OPTION_SPECS = {
    langCode: {
        expectedType: 'string',
        defaultValue: 'en',
        required: true
    },
    deviceType: {
        expectedType: 'string',
        required: true
    },
    appView: {
        expectedType: 'string',
        defaultValue: 'web',
        required: true
    },
    teamId: {
        expectedType: 'number',
        defaultValue: 0,
        required: true
    },
    fqSiteCorePrefixUrl: {
        expectedType: 'string',
        defaultValue: '',
        required: true
    },
    clientSide: {
        expectedType: 'boolean',
        defaultValue: true,
        required: true
    },
    windowOrReq: {
        expectedType: 'object',
        defaultValue: {},
        required: true
    },
    getLangCodePathElFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.getPathEl,
        required: false
    },
    getAppViewPathElFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.getPathEl,
        required: false
    },
    getTeamIdPathElFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.getPathEl,
        required: false
    },
    buildLinkFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.buildLink,
        required: false
    },
    buildPlayerPageLinkFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.buildPlayerPageLink,
        required: false
    },
    buildTeamHomepageLinkFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.buildTeamHomepageLink,
        required: false
    },
    buildPlayerSlugFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.buildPlayerSlug,
        required: false
    },
    buildTeamScheduleLinkFn: {
        expectedType: 'function',
        defaultValue: DefaultFunctions.buildTeamScheduleLink,
        required: false
    }
};

const defaultOptions = Helpers.getDefaultOptions(SUPPORTED_OPTION_SPECS);

/**
 * Property-agnostic base class for property-specific linker classes such as MlbLinker which operate
 * on data from the Sports Data API ("statsapi").
 *
 * _Nothing_ property-specific belongs in this base class.
 */
class BamLinker {

    /**
     *
     * @param {BamTeamData} bamTeamData An instance of [BamTeamData](https://github.mlbam.net/fed-packages/bam-team-data/blob/master/docs/index.md#bamteamdata) or subclass
     * @param {object} options Options as follows (optional unless noted otherwise):<br/><br/>
     *
     *   Note: several of the options below are functions (whose defaults are in this package's `DefaultFunctions` class).
     *   Overriding the functionality of this base class in subclasses may be accomplished by specifying different
     *   functions in the options you pass to this constructor; however, overriding specific subclass instance methods
     *   may make more sense.
     *  <ul>
     *    <li><i>langCode:</i> **Required.** The language code for the current request. Default: 'en'.</li>
     *    <li><i>appView:</i> **Required.** The app-view for the current request ('web', 'app', etc.). Handling of appViews may end
     *      up being handled mostly in subclasses; depends on whether or not various BANE sites handle them in the same
     *      way.</li>
     *    <li><i>deviceType:</i> **Required.** The type of client; valid values are 'desktop', 'tablet', and 'phone'.</li>
     *    <li><i>teamId:</i> **Required.** The team ID of the current request. Zero typically means a league site; a
     *      positive number would indicate that we're on a specific team's website.</li>
     *    <li><i>clientSide:</i> **Required.** Boolean: true if we're client-side, false if we're server-side.</li>
     *    <li><i>windowOrReq:</i> **Required.** A BANE "req" or a browser window object.</li>
     *    <li><i>getLangCodePathElFn:</i> A function which determines how to construct a path segment like '/fr' when
     *      passed the langCode of the current request, e.g. 'fr'.  This function takes four args; see the default for a
     *      sample implementation. Default: `DefaultFunctions.getPathEl`.</li>
     *    <li><i>getAppViewPathElFn:</i> Like the previous item, but for the current appView. Default: same as
     *      previous.</li>
     *    <li><i>getTeamIdPathElFn:</i> Like the previous item, but for the current teamId (if present). Default:
     *    same as previous.</li>
     *    <li><i>buildLinkFn:</i> A function which, passed a segment such as '/news/12121212', generates a URL based on
     *      the current request.  This function is used for generic links; player-page links and team-homepage links
     *      have their own functions (see next two items).  Default: `DefaultFunctions.buildLink`.</li>
     *    <li><i>buildPlayerPageLinkFn:</i> Like the previous item, but specifically for player-page links.
     *      Default: `DefaultFunctions.buildPlayerPageLink`.</li>
     *    <li><i>buildPlayerSlugFn:</i> Builds a player slug, for use in player-page links; format is
     *      property-dependent. Default: `DefaultFunctions.buildPlayerSlug`.</li>
     *    <li><i>buildTeamHomepageLinkFn:</i> Like the previous item, but specifically for team-homepage links.
     *      Default: `DefaultFunctions.buildTeamHomepageLink`.</li>
     *  </ul>
     */

    constructor(bamTeamData, options) {
        Helpers.validateOptions(options, SUPPORTED_OPTION_SPECS); // throw error if options not valid
        const opts = Object.assign(cloneDeep(defaultOptions), options || {}); // now we have default vals for missing options
        this._teamData = bamTeamData;
        this._langCode = opts.langCode;
        this._deviceType = opts.deviceType;
        this._appView = opts.appView;
        this._teamId = opts.teamId;
        this._clientSide = opts.clientSide;
        this._windowOrReq = opts.windowOrReq;
        this._langCodePathEl = opts.getLangCodePathElFn(this, this._langCode,
            SUPPORTED_OPTION_SPECS.langCode.defaultValue, false);
        this._appViewPathEl = opts.getAppViewPathElFn(this, this._appView,
            SUPPORTED_OPTION_SPECS.appView.defaultValue, false);
        this._teamPathEl = opts.getTeamIdPathElFn(this, this._teamId,
            SUPPORTED_OPTION_SPECS.teamId.defaultValue, true);
        this._buildLinkFn = opts.buildLinkFn;
        this._buildPlayerPageLinkFn = opts.buildPlayerPageLinkFn;
        this._buildPlayerSlugFn = opts.buildPlayerSlugFn;
        this._buildTeamHomepageLinkFn = opts.buildTeamHomepageLinkFn;
        this._buildTeamScheduleLinkFn = opts.buildTeamScheduleLinkFn;

    }

    /**
     * Return a link, preserving language, app-view, and team (if keepTeam is true)
     * @param {string} urlSegment e.g. /schedule; should never contain app-view or lang path segments (/fr, /app, etc.).
     * Forward-slash will be prepended if not passed.
     * @param {boolean} keepTeam If true, the team-slug URL segment (if any) will be preserved. Default is true.
     * @return {string}
     */
    getLink(urlSegment, keepTeam) {
        BamArgValidator.validateArgs(arguments, ['urlSegment', 'keepTeam'], ['string', 'boolean'], [true, false]);
        let keepTeamPathElement = (keepTeam === undefined ? true : keepTeam);

        let segment = '' + urlSegment;
        if (segment.charAt(0) !== '/') {
            segment = '/' + segment;
        }
        return this._buildLinkFn(this, segment, keepTeamPathElement);
    }

    /**
     * Returns a player-page link based on a player's statsapi Person node.
     * @param {string | number} statsapiPerson A player's person node as returned by statsapi
     * @param {string | number} teamId The player's team ID.  Whether or not this param is required depends on the
     *   property.
     * @return {string} The player-page link with a slug like 'first-last-12345678'
     */
    getPlayerPageLink(statsapiPerson, teamId) {
        BamArgValidator.validateArgs(arguments, ['statsapiPerson', 'teamId'], ['object', 'string|number'],
            [true, false]);
        BamArgValidator.validateStatsapiPerson(statsapiPerson);
        return this._buildPlayerPageLinkFn(this, statsapiPerson, statsapiPerson.fullName, statsapiPerson.id, teamId);
    }

    /**
     * Returns a player-page link based on a player's name and ID
     * @param fullname e.g. John Smith; required
     * @param playerId Numeric player ID; required
     * @param teamId Numeric player ID; Whether or not this param is required depends on the property
     */
    getPlayerPageLinkByNameAndId(fullname, playerId, teamId) {
        BamArgValidator.validateArgs(arguments, ['fullname', 'playerId', 'teamId'],
            ['string', 'string|number', 'string|number'],
            [true, true, false]);
        return this._buildPlayerPageLinkFn(this, null, fullname, playerId, teamId);
    }

    /**
     * Returns a player-page slug based on the statsapi Person node
     * @param statsapiPerson A person node returned by the statsapi
     * @return {string} E.g. 'john-smith-12345678'
     */
    getPlayerSlug(statsapiPerson) {
        BamArgValidator.validateStatsapiPerson(statsapiPerson);
        return this._buildPlayerSlugFn(this, statsapiPerson, statsapiPerson.fullName, statsapiPerson.id);
    }

    /**
     * Returns a player-page slug based on player name and ID
     * @param fullname e.g. John Smith; required
     * @param playerId Numeric player ID; required;
     * @return {string} E.g. 'john-smith-12345678'
     */
    getPlayerSlugByNameAndId(fullname, playerId) {
        BamArgValidator.validateArgs(arguments, ['fullname', 'playerId'], ['string', 'string|number'], [true, true]);
        return this._buildPlayerSlugFn(this, null, fullname, playerId);
    }


    /**
     * Returns a team-homepage link based on the specified team ID
     * @param {number | string} teamId Numeric team id, as a number or string
     * @return {string} The link, or null if the team is not recognized as an active team
     */
    getTeamHomepageLinkFromTeamId(teamId) {
        BamArgValidator.validateArgs(arguments, ['teamId'], ['string|number'], [true]);
        return this._buildTeamHomepageLinkFn(this, teamId);
    }

    /**
     * Returns a team-homepage link based on the specified team ID
     * @param {number | string} teamId Numeric team id, as a number or string
     * @return {string} The link, or null if the team is not recognized as an active team
     */
    getTeamScheduleLinkFromTeamId(teamId) {
        BamArgValidator.validateArgs(arguments, ['teamId'], ['string|number'], [true]);
        return this._buildTeamScheduleLinkFn(this, teamId);
    }

    /**
     * Returns a team-homepage link based on the statsapi team node
     * @param {object} statsapiTeam A statsapi team node, such as returned the Sports Data Teams or Schedule API
     * @return {string} The link, or null if the team is not recognized as an active team
     */
    getTeamHomepageLink(statsapiTeam) {
        BamArgValidator.validateStatsapiTeam(statsapiTeam);
        return this.getTeamHomepageLinkFromTeamId(statsapiTeam.id);
    }

    /**
     * Returns a team slug such as "rangers" or "yankees"
     * @param {string | number} teamId A numeric team ID, as a string or number
     * @return {string} A team slug, or null if this is not a recognized active team.
     */
    getTeamSlugFromTeamId(teamId) {
        BamArgValidator.validateNumber(teamId);
        return this._teamData.getTeamSlug && this._teamData.getTeamSlug(parseInt(teamId));
    }

    /**
     * Returns a team slug such as "rangers" or "yankees" based on the statsapi team node
     * @param {object} statsapiTeam A statsapi team node, such as returned the Sports Data Teams or Schedule API
     * @return {string} The team slug, or null if the team is not recognized as an active team
     */
    getTeamSlug(statsapiTeam) {
        BamArgValidator.validateStatsapiTeam(statsapiTeam);
        return this.getTeamSlugFromTeamId(statsapiTeam.id);
    }

    /**
     * Returns a fully qualified team-logo-image URL.  Note that referencing team logos directly by URL may not be
     * the optimal way to use them; e.g. MLB uses sprites for most logo use cases.
     *
     * This superclass implementation throws an Error if called; subclasses must provide an implementation.
     *
     * @param {number | string} The numeric team ID, as a string or number. Pass zero for the league logo.
     * @param {boolean} darkBg Optional; true means the version of the logo meant for display on a dark background. The
     *   default is true.  Ignored if logos for the subclass property are not available in bark and light versions.
     * @param {string} logoType Optional, and may not apply to all properties.  MLB makes several logo types available
     *   (primary, hat, url, etc.), e.g.
     * @return {string}
     */
    getFqTeamLogoUrl(teamId, darkBg, logoType) {
        Helpers.throwNotImplementedError('getFqTeamLogoUrl');
    }

    /**
     * Return the BamTeamData subclass backing this BamLinker
     * @return {BamTeamData}
     */
    getBamTeamData() {
        return this._teamData;
    }

    /**
     * Return the fully qualified URL of the site-core static-assets root, e.g. (for MLB)
     * //www.mlbstatic.com/mlb.com/builds/site-core/fe87f5c56891104097b778aa06b60f1a369ec4fd_1494355291.  The returned
     * URL will not have a trailing slash.  Strings starting with '/images', '/fonts', '/bower_components', '/scripts',
     * etc. are expected to be appended to the returned URL.  Usage:
     *
     * var imageUrl = linker.getFqPathToSiteCoreAssets() + '/images/globallyUsedImage.png';
     *
     * Note that this method cannot be used to reference section-specific assets; these URLs have a different
     * structure, and also contain different build versions.  For that, use getFqPathToSectionAssets().
     *
     * This superclass implementation throws an Error if called; subclasses must provide an implementation.
     *
     * @return {string}
     */
    getFqPathToSiteCoreAssets() {
        Helpers.throwNotImplementedError('getFqPathToSiteCoreAssets');
    }

    /**
     * Return the fully qualified URL of the current section's static-assets root, e.g. (for MLB)
     * //www.mlbstatic.com/mlb.com/sections/club-schedule/builds/c47203dcaf2560531a98f3471b9972f1b9e6f492_1494356470.
     * The returned URL will not have a trailing slash.  Strings starting with '/images' or any other directories your
     * section exposes in its section_config.json are expected to be appended to the returned URL.  Usage:
     *
     * var imageUrl = linker.getFqPathToSectionAssets() + '/images/sectionSpecificImage.png';
     *
     * Note that this method cannot be used to reference global (site-core) assets; these URLs have a different
     * structure, and also contain different build versions.  For that, use getFqPathToSiteCoreAssets().
     *
     * This superclass implementation throws an Error if called; subclasses must provide an implementation.
     *
     * @return {string}
     */
    getFqPathToSectionAssets() {
        Helpers.throwNotImplementedError('getFqPathToSectionAssets');
    }

    /**
     /**
     * Static method which, passed either a BANE-controller <code>req</code> or a BANE browser <code>window</code>,
     * returns an array of teams
     * in statsapi format (see e.g. http://qa-statsapi.mlb.com/api/v1/teams/?sportId=1).  Returns null if a team array
     * could not be found.
     * @param {object} ctx A BANE controller request or browser window
     * @param clientArrAsDotNotatedStr e.g. "team_info" if arr is window.team_info
     * @param serverArrAsDotNotatedStr e.g. "path.to.teamArr" if arr is req.path.to.teamArr
     * @return {Array} A copy of the array found in ctx, or null
     */
    static getTeamArrayFromReqOrWindow(ctx, clientArrAsDotNotatedStr, serverArrAsDotNotatedStr) {
        return BamTeamData.getTeamArrayFromReqOrWindow(ctx, clientArrAsDotNotatedStr, serverArrAsDotNotatedStr);
    }

    static getHelpers() {
        return Helpers;
    }

}

export default BamLinker;

